import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, MailCheck, Fingerprint } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startRegistration } from "@simplewebauthn/browser";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SecurityPage() {
  const nav = useNavigate();
  const { user, setAuthOpen, errMsg } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingV, setSendingV] = useState(false);
  const [bio, setBio] = useState({ enabled: false, busy: false });

  React.useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  React.useEffect(() => {
    if (user && user !== false) api.get("/webauthn/status").then((r) => setBio((b) => ({ ...b, enabled: r.data.enabled }))).catch(() => {});
  }, [user]);
  if (!user || user === false) return <div className="p-8 text-center text-slate-500">Kyçu për të parë sigurinë.</div>;

  const changePw = async (e) => {
    e.preventDefault();
    if (nw !== nw2) { toast.error("Fjalëkalimet e reja nuk përputhen"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Fjalëkalimi u ndryshua");
      setCur(""); setNw(""); setNw2("");
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setSaving(false); }
  };

  const sendVerify = async () => {
    setSendingV(true);
    try { await api.post("/auth/send-verification"); toast.success("Të dërguam një email verifikimi"); }
    catch (e2) { toast.error(errMsg(e2)); }
    finally { setSendingV(false); }
  };

  const enableBio = async () => {
    setBio((b) => ({ ...b, busy: true }));
    try {
      const opts = (await api.post("/webauthn/register/begin")).data;
      const att = await startRegistration({ optionsJSON: opts });
      await api.post("/webauthn/register/complete", att);
      setBio({ enabled: true, busy: false });
      toast.success("Biometrika u aktivizua");
    } catch (e2) {
      setBio((b) => ({ ...b, busy: false }));
      toast.error(e2?.response?.data?.detail || "Aktivizimi i biometrikës dështoi");
    }
  };
  const disableBio = async () => {
    setBio((b) => ({ ...b, busy: true }));
    try { await api.delete("/webauthn/credentials"); setBio({ enabled: false, busy: false }); toast.success("Biometrika u çaktivizua"); }
    catch { setBio((b) => ({ ...b, busy: false })); }
  };

  const inp = "rounded-xl h-11";

  return (
    <div className="gs-fade px-4 pt-4 pb-6" data-testid="security-page">
      <button onClick={() => nav("/profil")} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />Profili</button>
      <h1 className="text-2xl font-extrabold text-slate-900 mb-4">Siguria</h1>

      {/* Email verification */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><MailCheck className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-semibold text-slate-800 text-sm">Verifikimi i email-it</div>
            <div className="text-xs text-slate-400">{user.verified ? "Email-i yt është i verifikuar" : "Email-i yt nuk është verifikuar"}</div>
          </div>
          {user.verified
            ? <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">Verifikuar</span>
            : <Button data-testid="send-verify-btn" onClick={sendVerify} disabled={sendingV} className="h-9 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-xs px-3">{sendingV ? "..." : "Dërgo email"}</Button>}
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-9 h-9 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center"><Lock className="w-4 h-4" /></span>
          <div className="font-semibold text-slate-800">Ndrysho fjalëkalimin</div>
        </div>
        <form onSubmit={changePw} className="space-y-2.5">
          <Input data-testid="cur-password" type="password" placeholder="Fjalëkalimi aktual" value={cur} onChange={(e) => setCur(e.target.value)} required className={inp} />
          <Input data-testid="new-password" type="password" placeholder="Fjalëkalimi i ri" value={nw} onChange={(e) => setNw(e.target.value)} required className={inp} />
          <Input data-testid="new-password2" type="password" placeholder="Konfirmo fjalëkalimin e ri" value={nw2} onChange={(e) => setNw2(e.target.value)} required className={inp} />
          <Button data-testid="change-password-btn" type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 font-semibold">{saving ? "Duke ruajtur..." : "Ruaj fjalëkalimin e ri"}</Button>
        </form>
      </div>

      {/* Biometric login */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 rounded-full flex items-center justify-center ${bio.enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}><Fingerprint className="w-5 h-5" /></span>
          <div className="flex-1">
            <div className="font-semibold text-slate-800 text-sm">Hyrje me gisht / fytyrë</div>
            <div className="text-xs text-slate-400">{bio.enabled ? "Aktive në këtë pajisje" : "Hyr shpejt pa fjalëkalim"}</div>
          </div>
          {bio.enabled
            ? <button data-testid="disable-bio-btn" onClick={disableBio} disabled={bio.busy} className="text-xs font-semibold text-red-500 bg-red-50 px-3 py-2 rounded-xl">Çaktivizo</button>
            : <Button data-testid="enable-bio-btn" onClick={enableBio} disabled={bio.busy} className="h-9 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-xs px-3">{bio.busy ? "..." : "Aktivizo"}</Button>}
        </div>
      </div>
    </div>
  );
}
